import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Grid,
  Box,
  CircularProgress,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  useMediaQuery,
  TableContainer,
  Paper,
  Typography,
  Avatar,
  Chip,
  Card,
  CardContent,
  InputAdornment,
  Tooltip,
  Fade,
  Zoom,
  alpha,
  Container,
  Breadcrumbs,
  Link,
  Badge,
} from "@mui/material";
import { collection, getDocs, doc, updateDoc, deleteDoc, writeBatch, query, where } from "firebase/firestore";
import { db } from "../firebase-config";
import Collections from "../collections";
import Header from "../components/header";
import {
  Edit,
  Save,
  Cancel,
  Person,
  Email,
  Phone,
  Lock,
  Restaurant,
  LocationOn,
  Search,
  Refresh,
  CheckCircle,
  ErrorOutline,
  ArrowBack,
  Home,
  People,
  Visibility,
  VisibilityOff,
  Delete,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';

const ManageUsers = () => {
  const theme = useTheme();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [editMode, setEditMode] = useState(null);
  const [editedUser, setEditedUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showPassword, setShowPassword] = useState({});
  const [toast, setToast] = useState({ open: false, message: "", severity: "" });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const isMobile = useMediaQuery("(max-width:600px)");
  const isTablet = useMediaQuery("(max-width:960px)");

  // Fetch all users from Firestore
  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const usersSnapshot = await getDocs(collection(db, Collections.USERS));
      const usersList = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setUsers(usersList);
      setFilteredUsers(usersList);
    } catch (error) {
      console.error("Error fetching users: ", error);
      setToast({
        open: true,
        message: "Error fetching users",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle search
  useEffect(() => {
    const filtered = users.filter(user =>
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.phone?.includes(searchTerm) ||
      user.restaurantName?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredUsers(filtered);
  }, [searchTerm, users]);

  const handleEdit = (user) => {
    setEditMode(user.id);
    setEditedUser({ ...user });
  };

  const handleSave = async (id) => {
    try {
      // Get the original user data to compare restaurantName changes
      const originalUser = users.find(u => u.id === id);
      const oldRestaurantName = originalUser?.restaurantName;
      const newRestaurantName = editedUser.restaurantName;

      // Update user document
      const userDocRef = doc(db, Collections.USERS, id);
      await updateDoc(userDocRef, editedUser);

      // Update all related invoices if restaurantName changed
      if (oldRestaurantName && oldRestaurantName !== newRestaurantName) {
        try {
          const invoicesQuery = query(
            collection(db, Collections.INVOICES),
            where("restaurantName", "==", oldRestaurantName)
          );
          const invoicesSnapshot = await getDocs(invoicesQuery);

          if (!invoicesSnapshot.empty) {
            const batch = writeBatch(db);
            invoicesSnapshot.docs.forEach((invoiceDoc) => {
              batch.update(invoiceDoc.ref, {
                restaurantName: newRestaurantName,
              });
            });
            await batch.commit();
          }
        } catch (invoiceErr) {
          console.error("Error updating invoices:", invoiceErr);
        }
      }

      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === id ? { ...user, ...editedUser } : user
        )
      );
      setFilteredUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === id ? { ...user, ...editedUser } : user
        )
      );
      setToast({
        open: true,
        message: `User updated successfully!${oldRestaurantName !== newRestaurantName ? ' Invoices updated.' : ''}`,
        severity: "success",
      });
    } catch (error) {
      console.error("Error updating user: ", error);
      setToast({
        open: true,
        message: "Error updating user",
        severity: "error",
      });
    } finally {
      setEditMode(null);
    }
  };

  // Delete user
  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    setDeleting(true);
    try {
      await deleteDoc(doc(db, Collections.USERS, userToDelete.id));
      setUsers(prev => prev.filter(u => u.id !== userToDelete.id));
      setFilteredUsers(prev => prev.filter(u => u.id !== userToDelete.id));
      setToast({
        open: true,
        message: `User "${userToDelete.name || userToDelete.restaurantName}" deleted successfully!`,
        severity: "success",
      });
    } catch (error) {
      console.error("Error deleting user: ", error);
      setToast({
        open: true,
        message: "Error deleting user",
        severity: "error",
      });
    } finally {
      setDeleting(false);
      setDeleteConfirmOpen(false);
      setUserToDelete(null);
    }
  };

  const handleCancel = () => {
    setEditMode(null);
    setEditedUser(null);
  };

  const handleChange = (e) => {
    setEditedUser({
      ...editedUser,
      [e.target.name]: e.target.value,
    });
  };

  const togglePasswordVisibility = (userId) => {
    setShowPassword(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const getInitials = (name) => {
    return name
      ?.split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'U';
  };

  const getStatusChip = (user) => {
    // You can customize this based on your user data structure
    const hasAllFields = user.name && user.email && user.phone && user.restaurantName;
    return (
      <Chip
        size="small"
        icon={hasAllFields ? <CheckCircle /> : <ErrorOutline />}
        label={hasAllFields ? "Active" : "Incomplete"}
        color={hasAllFields ? "success" : "warning"}
        sx={{
          height: 24,
          '& .MuiChip-icon': {
            fontSize: 16,
            marginLeft: 0.5,
            marginRight: -0.5
          }
        }}
      />
    );
  };

  const renderEditFields = () => (
    <>
      <TableCell>
        <TextField
          name="name"
          value={editedUser.name || ''}
          onChange={handleChange}
          size="small"
          variant="outlined"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Person sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.primary.light, 0.1),
            }
          }}
        />
      </TableCell>
      <TableCell>
        <TextField
          name="password"
          value={editedUser.password || ''}
          onChange={handleChange}
          size="small"
          type={showPassword[editedUser.id] ? 'text' : 'password'}
          variant="outlined"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Lock sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => togglePasswordVisibility(editedUser.id)}
                  edge="end"
                  size="small"
                >
                  {showPassword[editedUser.id] ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.primary.light, 0.1),
            }
          }}
        />
      </TableCell>
      <TableCell>
        <TextField
          name="email"
          value={editedUser.email || ''}
          onChange={handleChange}
          size="small"
          variant="outlined"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Email sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.primary.light, 0.1),
            }
          }}
        />
      </TableCell>
      <TableCell>
        <TextField
          name="phone"
          value={editedUser.phone || ''}
          onChange={handleChange}
          size="small"
          variant="outlined"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Phone sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.primary.light, 0.1),
            }
          }}
        />
      </TableCell>
      <TableCell>
        <TextField
          name="restaurantName"
          value={editedUser.restaurantName || ''}
          onChange={handleChange}
          size="small"
          variant="outlined"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Restaurant sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.primary.light, 0.1),
            }
          }}
        />
      </TableCell>
      <TableCell>
        <TextField
          name="address"
          value={editedUser.address || ''}
          onChange={handleChange}
          size="small"
          variant="outlined"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LocationOn sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.primary.light, 0.1),
            }
          }}
        />
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Save" arrow TransitionComponent={Zoom}>
            <IconButton
              onClick={() => handleSave(editedUser.id)}
              size="small"
              sx={{
                backgroundColor: theme.palette.success.light,
                color: theme.palette.success.main,
                '&:hover': {
                  backgroundColor: theme.palette.success.main,
                  color: 'white',
                },
                transition: 'all 0.2s',
              }}
            >
              <Save fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Cancel" arrow TransitionComponent={Zoom}>
            <IconButton
              onClick={handleCancel}
              size="small"
              sx={{
                backgroundColor: theme.palette.error.light,
                color: theme.palette.error.main,
                '&:hover': {
                  backgroundColor: theme.palette.error.main,
                  color: 'white',
                },
                transition: 'all 0.2s',
              }}
            >
              <Cancel fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </>
  );

  const renderUserRow = (user) => (
    <TableRow
      key={user.id}
      sx={{
        '&:hover': {
          backgroundColor: alpha(theme.palette.primary.light, 0.1),
          transition: 'background-color 0.2s',
        },
        '& td': {
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }
      }}
    >
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor: user.email ? theme.palette.success.main : theme.palette.warning.main,
                  border: '2px solid white',
                }}
              />
            }
          >
            <Avatar
              sx={{
                bgcolor: theme.palette.primary.main,
                width: 36,
                height: 36,
                fontSize: '1rem',
                fontWeight: 600,
              }}
            >
              {getInitials(user.name)}
            </Avatar>
          </Badge>
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {user.name || 'N/A'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ID: {user.id.slice(-6)}
            </Typography>
          </Box>
        </Box>
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Lock sx={{ color: theme.palette.text.secondary, fontSize: 16 }} />
          <Typography variant="body2">
            {showPassword[user.id] ? user.password : '••••••••'}
          </Typography>
          <IconButton
            size="small"
            onClick={() => togglePasswordVisibility(user.id)}
            sx={{ ml: 0.5 }}
          >
            {showPassword[user.id] ?
              <VisibilityOff fontSize="small" /> :
              <Visibility fontSize="small" />
            }
          </IconButton>
        </Box>
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Email sx={{ color: theme.palette.text.secondary, fontSize: 16 }} />
          <Typography variant="body2">{user.email || 'N/A'}</Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Phone sx={{ color: theme.palette.text.secondary, fontSize: 16 }} />
          <Typography variant="body2">{user.phone || 'N/A'}</Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Restaurant sx={{ color: theme.palette.text.secondary, fontSize: 16 }} />
          <Typography variant="body2">{user.restaurantName || 'N/A'}</Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <LocationOn sx={{ color: theme.palette.text.secondary, fontSize: 16 }} />
          <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
            {user.address || 'N/A'}
          </Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
          {getStatusChip(user)}
          <Tooltip title="Edit User" arrow TransitionComponent={Zoom}>
            <IconButton
              onClick={() => handleEdit(user)}
              size="small"
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.primary.main,
                  color: 'white',
                },
                transition: 'all 0.2s',
              }}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete User" arrow TransitionComponent={Zoom}>
            <IconButton
              onClick={() => handleDeleteClick(user)}
              size="small"
              sx={{
                backgroundColor: alpha(theme.palette.error.main, 0.1),
                color: theme.palette.error.main,
                '&:hover': {
                  backgroundColor: theme.palette.error.main,
                  color: 'white',
                },
                transition: 'all 0.2s',
              }}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );

  return (
    <Box sx={{
      minHeight: '100vh',
      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.2)} 0%, ${alpha(theme.palette.secondary.light, 0.2)} 100%)`,
    }}>
      <Header title="Manage Users" />

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            href="/"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, textDecoration: 'none' }}
          >
            <Home sx={{ fontSize: 18 }} />
            Home
          </Link>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <People sx={{ fontSize: 18 }} />
            Users
          </Box>
        </Breadcrumbs>

        {/* Header Card */}
        <Card
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography variant="h4" fontWeight={600} gutterBottom>
                  User Management
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  Manage and monitor all registered users in one place
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', justifyContent: { md: 'flex-end' }, gap: 2 }}>
                  <Chip
                    icon={<People />}
                    label={`${filteredUsers.length} Total Users`}
                    sx={{
                      backgroundColor: alpha('#fff', 0.2),
                      color: 'white',
                      '& .MuiChip-icon': { color: 'white' },
                    }}
                  />
                  <Tooltip title="Refresh" arrow>
                    <IconButton
                      onClick={fetchUsers}
                      sx={{
                        backgroundColor: alpha('#fff', 0.2),
                        color: 'white',
                        '&:hover': { backgroundColor: alpha('#fff', 0.3) }
                      }}
                    >
                      <Refresh />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Search Bar */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <TextField
            placeholder="Search users by name, email, phone or restaurant..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: theme.palette.text.secondary }} />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              minWidth: 250,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              }
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {filteredUsers.length} result{filteredUsers.length !== 1 ? 's' : ''} found
          </Typography>
        </Paper>

        {/* Users Table */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{
                    backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                  }}>
                    {[
                      'User',
                      'Password',
                      'Email',
                      'Phone',
                      'Restaurant',
                      'Address',
                      'Actions'
                    ].map((header) => (
                      <TableCell
                        key={header}
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.text.primary,
                          py: 2,
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers.length > 0 ? (
                    filteredUsers.map((user) => (
                      editMode === user.id ? (
                        <TableRow key={user.id}>
                          {renderEditFields()}
                        </TableRow>
                      ) : (
                        renderUserRow(user)
                      )
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                        <Box>
                          <People sx={{ fontSize: 48, color: theme.palette.text.secondary, mb: 2 }} />
                          <Typography variant="h6" color="text.secondary" gutterBottom>
                            No users found
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {searchTerm ? 'Try adjusting your search term' : 'No users have been registered yet'}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* Toast Notification */}
        <Snackbar
          open={toast.open}
          autoHideDuration={4000}
          onClose={() => setToast({ ...toast, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          TransitionComponent={Fade}
        >
          <Alert
            onClose={() => setToast({ ...toast, open: false })}
            severity={toast.severity}
            variant="filled"
            sx={{
              width: '100%',
              borderRadius: 2,
              boxShadow: theme.shadows[8],
            }}
          >
            {toast.message}
          </Alert>
        </Snackbar>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={() => { setDeleteConfirmOpen(false); setUserToDelete(null); }}
          PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
        >
          <DialogTitle sx={{ fontWeight: 600 }}>Delete User</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete <strong>{userToDelete?.name || userToDelete?.restaurantName}</strong>?
              This action cannot be undone. The user's existing invoices will NOT be deleted.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => { setDeleteConfirmOpen(false); setUserToDelete(null); }}
              variant="outlined"
              sx={{ borderRadius: 2 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              variant="contained"
              color="error"
              disabled={deleting}
              startIcon={deleting ? <CircularProgress size={16} /> : <Delete />}
              sx={{ borderRadius: 2 }}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default ManageUsers;